<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import IconAttachMoney from '~icons/material-symbols/attach-money'
import IconCheck from '~icons/material-symbols/check'
import IconReceiptLongCheck from '~icons/material-symbols/receipt-long'
import Loading from '~/components/loading/index.vue'
import useSuppliers from '~/store-v2/payment-model/useSuppliers'

interface Props {
  isOpen: boolean
  initialTab?: 'partners' | 'non_partners'
}

const props = withDefaults(defineProps<Props>(), {
  initialTab: 'partners',
})

const emit = defineEmits<{
  'update:isOpen': [value: boolean]
}>()

const { t } = useI18n()

const partnersStepsSegments = computed(() => [
  { prefix: t('simulation.suppliers_modal.partners_step_0_prefix'), bold: t('simulation.suppliers_modal.partners_step_0_bold'), suffix: t('simulation.suppliers_modal.partners_step_0_suffix') },
  { prefix: t('simulation.suppliers_modal.partners_step_1_prefix'), bold: t('simulation.suppliers_modal.partners_step_1_bold'), suffix: t('simulation.suppliers_modal.partners_step_1_suffix') },
  { prefix: t('simulation.suppliers_modal.partners_step_2_prefix'), bold: t('simulation.suppliers_modal.partners_step_2_bold'), suffix: t('simulation.suppliers_modal.partners_step_2_suffix') },
  { prefix: t('simulation.suppliers_modal.partners_step_3_prefix'), bold: t('simulation.suppliers_modal.partners_step_3_bold'), suffix: t('simulation.suppliers_modal.partners_step_3_suffix') },
  { prefix: t('simulation.suppliers_modal.partners_step_4_prefix'), bold: t('simulation.suppliers_modal.partners_step_4_bold'), suffix: t('simulation.suppliers_modal.partners_step_4_suffix') },
])
const nonPartnersStepsSegments = computed(() => [
  { prefix: t('simulation.suppliers_modal.non_partners_step_0_prefix'), bold: t('simulation.suppliers_modal.non_partners_step_0_bold'), suffix: t('simulation.suppliers_modal.non_partners_step_0_suffix') },
  { prefix: t('simulation.suppliers_modal.non_partners_step_1_prefix'), bold: t('simulation.suppliers_modal.non_partners_step_1_bold'), suffix: t('simulation.suppliers_modal.non_partners_step_1_suffix') },
  { prefix: t('simulation.suppliers_modal.non_partners_step_2_prefix'), bold: t('simulation.suppliers_modal.non_partners_step_2_bold'), suffix: t('simulation.suppliers_modal.non_partners_step_2_suffix') },
])

const isModalOpen = ref(props.isOpen)
const selectedTab = ref(props.initialTab)

watch(() => props.isOpen, (newValue) => {
  isModalOpen.value = newValue
})

const tabListModel = ref([
  {
    key: 'partners',
    label: t('simulation.suppliers_modal.tabs_partners'),
    active: props.initialTab === 'partners',
  },
  {
    key: 'non_partners',
    label: t('simulation.suppliers_modal.tabs_non_partners'),
    active: props.initialTab === 'non_partners',
  },
])

const { data: suppliersData, isLoading: isLoadingSuppliers } = useSuppliers()

const partnerSuppliers = computed(() => {
  if (!suppliersData.value) return []
  return suppliersData.value.filter(s => s.is_partner !== false)
})

const nonPartnerSuppliers = computed(() => {
  if (!suppliersData.value) return []
  return suppliersData.value.filter(s => s.is_partner === false)
})

function handleTabChange(tab: { key: string }) {
  selectedTab.value = tab.key as 'partners' | 'non_partners'
  tabListModel.value = tabListModel.value.map(tabItem => ({
    ...tabItem,
    active: tabItem.key === tab.key,
  }))
}

watch(() => props.initialTab, (newTab) => {
  if (newTab) {
    selectedTab.value = newTab
    tabListModel.value = tabListModel.value.map(tabItem => ({
      ...tabItem,
      active: tabItem.key === newTab,
    }))
  }
}, { immediate: true })

function closeModal() {
  isModalOpen.value = false
  emit('update:isOpen', false)
}
</script>

<template>
  <SolModal
    id="suppliers-modal"
    class="suppliers-modal"
    :is-open="isModalOpen"
    :title="t('simulation.suppliers_modal.title')"
    :action-primary-text="t('simulation.suppliers_modal.button_ok')"
    :size="{
      desktop: 'large',
      mobile: 'bottom-sheet',
    }"
    data-testid="suppliers-modal"
    @close="closeModal"
    @action:primary="closeModal"
  >
    <div class="suppliers-modal-content">
      <p class="suppliers-modal-intro">
        {{ t('simulation.suppliers_modal.intro') }}
      </p>
      <SolTabs
        id="suppliers-tabs"
        v-model:tab-list="tabListModel"
        class="suppliers-tabs"
        @tabitem:active="handleTabChange"
      >
        <template #tab:partners>
          <div class="suppliers-tab-grid-wrapper">
            <div class="suppliers-tab-grid">
              <div class="suppliers-tab-left">
                <div class="suppliers-tab-left-block">
                  <div class="suppliers-tab-heading">
                    <div class="suppliers-tab-heading-item">
                      <IconCheck class="icon-suppliers-tab" />
                      {{ t('simulation.suppliers_modal.partners_heading_0') }}
                    </div>
                    <div class="suppliers-tab-heading-item">
                      <IconCheck class="icon-suppliers-tab" />
                      {{ t('simulation.suppliers_modal.partners_heading_1') }}
                    </div>
                  </div>
                  <p class="suppliers-tab-benefit">
                    {{ t('simulation.suppliers_modal.partners_benefit') }}
                  </p>
                </div>
                <div class="suppliers-how-it-works">
                  <p class="suppliers-how-it-works-title">
                    {{ t('simulation.suppliers_modal.how_it_works') }}
                  </p>
                  <ol class="suppliers-steps-list">
                    <li
                      v-for="(step, index) in partnersStepsSegments"
                      :key="index"
                      class="suppliers-step-item"
                    >
                      <span>{{ step.prefix }}</span><span class="suppliers-step-bold">{{ step.bold }}</span><span>{{ step.suffix }}</span>
                    </li>
                  </ol>
                </div>
              </div>
              <div class="suppliers-tab-right suppliers-panel-partners">
                <h4 class="suppliers-panel-title">
                  {{ t('simulation.suppliers_modal.list_partners_title') }}
                </h4>
                <div class="suppliers-list-area">
                  <div v-if="isLoadingSuppliers" class="flex justify-center items-center h-full py-6">
                    <Loading />
                  </div>
                  <div v-else-if="partnerSuppliers.length === 0" class="flex flex-col items-center justify-center h-full py-6">
                    <SolEmptyState
                      id="no-partner-suppliers"
                      variant="empty-list"
                      :title="t('simulation.suppliers_modal.empty_title')"
                      :subtitle="t('simulation.suppliers_modal.empty_subtitle')"
                    />
                  </div>
                  <ul v-else class="suppliers-simple-list" aria-label="Lista de fornecedores parceiros">
                    <li
                      v-for="supplier in partnerSuppliers"
                      :key="supplier.id"
                      class="suppliers-simple-list-item"
                    >
                      {{ supplier.company_name }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template #tab:non_partners>
          <div class="suppliers-tab-grid-wrapper">
            <div class="suppliers-tab-grid">
              <div class="suppliers-tab-left">
                <div class="suppliers-tab-left-block">
                  <div class="suppliers-tab-heading suppliers-tab-heading--warning">
                    <div class="suppliers-tab-heading-item">
                      <IconAttachMoney class="icon-suppliers-tab" />
                      {{ t('simulation.suppliers_modal.non_partners_heading_0') }}
                    </div>
                    <div class="suppliers-tab-heading-item">
                      <IconReceiptLongCheck class="icon-suppliers-tab" />
                      {{ t('simulation.suppliers_modal.non_partners_heading_1') }}
                    </div>
                  </div>
                  <p class="suppliers-tab-benefit">
                    {{ t('simulation.suppliers_modal.non_partners_benefit') }}
                  </p>
                </div>
                <div class="suppliers-how-it-works">
                  <p class="suppliers-how-it-works-title">
                    {{ t('simulation.suppliers_modal.how_it_works') }}
                  </p>
                  <ol class="suppliers-steps-list">
                    <li
                      v-for="(step, index) in nonPartnersStepsSegments"
                      :key="index"
                      class="suppliers-step-item"
                    >
                      <span>{{ step.prefix }}</span><span class="suppliers-step-bold">{{ step.bold }}</span><span>{{ step.suffix }}</span>
                    </li>
                  </ol>
                </div>
              </div>
              <div class="suppliers-tab-right suppliers-panel-others">
                <h4 class="suppliers-panel-title">
                  {{ t('simulation.suppliers_modal.list_others_title') }}
                </h4>
                <div class="suppliers-list-area">
                  <div v-if="isLoadingSuppliers" class="flex justify-center items-center h-full py-6">
                    <Loading />
                  </div>
                  <div v-else-if="nonPartnerSuppliers.length === 0" class="flex flex-col items-center justify-center h-full py-6">
                    <SolEmptyState
                      id="no-non-partner-suppliers"
                      variant="empty-list"
                      :title="t('simulation.suppliers_modal.empty_title')"
                      :subtitle="t('simulation.suppliers_modal.empty_subtitle')"
                    />
                  </div>
                  <ul v-else class="suppliers-simple-list" aria-label="Lista de fornecedores não parceiros">
                    <li
                      v-for="supplier in nonPartnerSuppliers"
                      :key="supplier.id"
                      class="suppliers-simple-list-item"
                    >
                      {{ supplier.company_name }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </template>
      </SolTabs>
    </div>
  </SolModal>
</template>

<style scoped lang="scss">
.suppliers-modal-content {
  .suppliers-modal-intro {
    @apply fonts-body-medium-regular text-neutral-low-medium mb-4;
  }

  .suppliers-tab-grid-wrapper {
    @apply flex flex-col gap-6;

    .suppliers-tab-grid {
      @apply grid gap-6 items-start;
      grid-template-columns: 1fr;

      @media (min-width: 768px) {
        grid-template-columns: 2fr 1fr;
      }

      .suppliers-tab-left {
        @apply flex flex-col gap-6;

        .suppliers-tab-left-block {
          @apply flex flex-col gap-4;

          .suppliers-tab-heading {
            @apply flex flex-col gap-4;
            @apply fonts-heading-h4 text-neutral-low-medium;

            .suppliers-tab-heading-item {
              @apply flex items-center gap-4;

              .icon-suppliers-tab {
                @apply flex-shrink-0 flex-grow-0 w-7 h-7 min-w-7 min-h-7 text-brand-secondary-pure;
              }
            }

            &.suppliers-tab-heading--warning .icon-suppliers-tab {
              @apply text-feedback-warning-pure;
            }
          }

          .suppliers-tab-benefit {
            @apply fonts-body-medium-regular text-neutral-low-medium;
          }
        }

        .suppliers-how-it-works {
          @apply flex flex-col gap-6;
          @apply border-t border-neutral-low-medium border-opacity-light pt-6;

          .suppliers-how-it-works-title {
            @apply fonts-body-medium-bold text-neutral-low-medium;
          }

          .suppliers-steps-list {
            @apply list-decimal pl-4  flex flex-col gap-5;

            .suppliers-step-item {
              @apply fonts-body-small-regular text-neutral-low-medium;

              .suppliers-step-bold {
                @apply font-bold;
              }
            }
          }
        }
      }

      .suppliers-tab-right {
        @apply flex flex-col gap-4 px-4 pt-4 pb-6 rounded-lg;
        @apply border border-neutral-low-medium border-opacity-light;

        &.suppliers-panel-partners {
          @apply bg-brand-secondary-medium bg-opacity-light;
        }

        &.suppliers-panel-others {
          @apply bg-neutral-high-light;
        }

        .suppliers-panel-title {
          @apply fonts-overline-small text-neutral-low-dark uppercase;
          font-style: normal;
          font-weight: 700;
          font-size: 10px;
        }

        .suppliers-list-area {
          @apply h-70 min-h-70;
        }

        .suppliers-simple-list {
          @apply flex flex-col gap-2;
          @apply overflow-y-auto overflow-x-hidden h-full;

          .suppliers-simple-list-item {
            @apply fonts-body-small-regular text-brand-primary-dark;
          }
        }
      }
    }
  }
}
</style>
